import { Component, Input, OnInit, ViewChild, Inject, Injector } from '@angular/core';
import { NgForm, NgModelGroup } from '@angular/forms';
import { MatTabGroup } from '@angular/material/tabs';
import { MatDialogRef, MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import _, { unset } from 'lodash';
import moment from 'moment';
import { BaseDialogComponent } from '../base-dialog/base-dialog.component';
import { BaseComponent } from '../base-form/base-form.component';
import { EventServicesEntityComponent } from '../event-services-entity/event-services-entity.component';
import { ReplaySubject, Subject, concatMap, debounceTime, distinctUntilChanged, filter, of, switchMap, tap } from 'rxjs';
import { ScheduleConflictsComponent } from '../../utility/schedule-conflicts/schedule-conflicts.component';
import { InternationalAddressService } from 'src/app/services/international-address.service';
import { PhoneNumberService, PhoneValidationResult } from 'src/app/services/phone-number.service';
import { CountryCode, E164Number } from 'libphonenumber-js';
import { LoggerService } from '../../../services/logger.service';

@Component({
  selector: 'app-student-inquiry-entity',
  templateUrl: './student-inquiry-entity.component.html',
  styleUrls: ['./student-inquiry-entity.component.css']
})
export class StudentInquiryEntityComponent extends BaseDialogComponent implements OnInit {

  // Will be injected by Angular DI
  internationalAddressService: InternationalAddressService;

  @ViewChild(ScheduleConflictsComponent) scheduleConflicts: ScheduleConflictsComponent;
  @ViewChild('firstContact') firstContact: NgModelGroup;
  @ViewChild('partnerContact') partnerContact: NgModelGroup;
  @ViewChild('f') formElement: NgForm;

  override eckType = "student_accounts";
  override bundle = "student_account";
  override entityId = this._injectedDialogData['data']['EntityID'];

  // Declare the subjects
  private DEPackageSubject = new ReplaySubject<any>(1);
  private PreOriginalPackageSubject = new ReplaySubject<any>(1);
  private PreOriginalCouplePackageSubject = new ReplaySubject<any>(1);

  // Public observables to expose the subjects
  public DEPackage$ = this.DEPackageSubject.asObservable();
  public PreOriginalPackage$ = this.PreOriginalPackageSubject.asObservable();
  public PreOriginalCouplePackage$ = this.PreOriginalCouplePackageSubject.asObservable();
  studentAccountReady: boolean = false;

  // Cached filtered lesson types to prevent infinite change detection
  private _filteredLessonTypes: any[] | null = null;

  contactList = [];
  field_contacts = [];
  checked = true;
  scheduleFirstLessonEnabled: boolean = true;
  conflictingEvent: any = null;
  alternativeTimes: any[] = [];
  field_attending_lessons: string = 'solo';
  private phoneFieldState: Record<'main' | 'partner', { home?: E164Number | null; cell?: E164Number | null }> = {
    main: {},
    partner: {}
  };

  disableSubmitButton = false;
  studentAccountCreated: boolean = false;
  studentAccountData;
  override field_date_and_time = this._injectedDialogData?.['data']?.['fieldsData']?.['dayview_field_date_and_time'] || "";
  // Do not prefill inquiry date from day view; default to today.
  field_inquired = moment().format('YYYY-MM-DD');
  _field_expiration_date = this._injectedDialogData?.['data']?.['fieldsData']?.['dayview_field_date_and_time'] || moment().format('YYYY-MM-DD');
  _field_sale_date = this._injectedDialogData?.['data']?.['fieldsData']?.['dayview_field_date_and_time'] || moment().format('YYYY-MM-DD');
  _inquiry = this._injectedDialogData?.['data']?.['fieldsData']?.['inquiry'];

  field_communication_opt_in: boolean = false;
  field_communication_opt_in_partner: boolean = false;

  duplicateWarning: string = '';
  duplicateWarningPartner: string = '';

  // Address internationalization
  selectedCountry = 'US';
  selectedCountryPartner = 'US';
  administrativeAreaLabel = 'State';
  postalCodeLabel = 'ZIP Code';
  administrativeAreaLabelPartner = 'State';
  postalCodeLabelPartner = 'ZIP Code';

  /**
   * Handle country change to update labels
   */
  onCountryChange(countryCode: string) {
    this.selectedCountry = countryCode;
    this.administrativeAreaLabel = this.internationalAddressService.getAdministrativeAreaLabel(countryCode);
    this.postalCodeLabel = this.internationalAddressService.getPostalCodeLabel(countryCode);
  }

  /**
   * Handle partner country change to update labels
   */
  onCountryChangePartner(countryCode: string) {
    this.selectedCountryPartner = countryCode;
    this.administrativeAreaLabelPartner = this.internationalAddressService.getAdministrativeAreaLabel(countryCode);
    this.postalCodeLabelPartner = this.internationalAddressService.getPostalCodeLabel(countryCode);
  }

  onNotificationPreferenceChange(contactIndex: number, value: string) {
    LoggerService.log('Selected Communication Preference:', value);
    this.field_contacts[contactIndex].field_notification_preference = value;
  }

  onNotificationPreferenceChangePartner(value: string) {
    LoggerService.log('Selected Communication Preference for Partner:', value);
    this.partnerContact.value.field_notification_preference = value;
  }

  onCommunicationOptInChange(value: boolean) {
    LoggerService.log('onCommunicationOptInChange called', value)
    this.field_communication_opt_in = value;
  }

  onCommunicationOptInPartnerChange(value: boolean) {
    LoggerService.log('onCommunicationOptInPartnerChange called', value)
    this.field_communication_opt_in_partner = value;
  }

  override async onSubmit(f: NgForm, promptScheduleService = false) {
    LoggerService.log('onSubmit called');
    LoggerService.log('firstContact:', this.firstContact);

    // Mark all form controls as touched to trigger validation
    Object.keys(f.controls).forEach(key => {
      f.controls[key].markAsTouched();
    });

    // Check if form is valid
    if (!this.isFormValid()) {
      this.disableSubmitButton = false;
      return;
    }

    this.displayProgressSpinner(true);
    this.disableSubmitButton = true;
    // Copy the object to not modify binded data.
    let values = _.cloneDeep(f.form.value);
    let requested_date_and_time = moment(values.requested_date_time).format('YYYY-MM-DDTHH:mm:ss');

    this.__alterValues(values);

    // Fix for "field_teacher" being a required field when it should not.
    if (values.field_teacher == "") { delete values.field_teacher; }

    // Use the requested date and time (for the enrollment).
    values.field_inquired = this.field_inquired; // contact date
    this._field_expiration_date = values.requested_date_time;
    this._field_sale_date = values.requested_date_time;

    // Build main contact address only if at least one field is filled
    const mainAddress = f.value.__field_contact_inline_form?.field_address;
    const hasMainAddress = mainAddress?.address_line1 || mainAddress?.locality ||
                          mainAddress?.administrative_area || mainAddress?.postal_code;

    let mainContact: any = {
      type: 'contacts',
      bundle: 'student_record',
      field_attending_lessons: this.field_attending_lessons,
      field_home_phone: this.phoneFieldState.main.home || this.filterPhoneNumber(f.value.__field_contact_inline_form?.field_home_phone, this.selectedCountry as CountryCode),
      field_cell_phone: this.phoneFieldState.main.cell || this.filterPhoneNumber(f.value.__field_contact_inline_form?.field_cell_phone, this.selectedCountry as CountryCode),
      field_first_name: f.value.__field_contact_inline_form?.field_first_name,
      field_last_name: f.value.__field_contact_inline_form?.field_last_name,
      field_email: f.value.__field_contact_inline_form?.field_email,
      field_lead_follow: f.value.__field_contact_inline_form?.field_lead_follow,
      field_communication_opt_in: this.field_communication_opt_in ? "1" : "0",
      field_studio_reference: [this._authService.selectedStudio?.id],
      // field_notification_preference: f.value.__field_contact_inline_form?.field_notification_preference,
    };

    // Only include gender if it has a value (it's a boolean field)
    const genderMain = f.value.__field_contact_inline_form?.field_gender_main;
    if (genderMain !== '' && genderMain !== null && genderMain !== undefined) {
      mainContact.field_gender = genderMain;
    }

    // Only include address if at least one field is filled
    if (hasMainAddress) {
      mainContact.field_address = {
        address_line1: mainAddress?.address_line1,
        locality: mainAddress?.locality,
        administrative_area: mainAddress?.administrative_area,
        postal_code: mainAddress?.postal_code,
        country_code: mainAddress?.country_code || this.selectedCountry,
      };
    }

    let partnerContact = null;
    if (this.field_attending_lessons === 'partner') {
      // Build partner contact address only if at least one field is filled
      const partnerAddress = this.partnerContact.value.field_address;
      const hasPartnerAddress = partnerAddress?.address_line1 || partnerAddress?.locality ||
                               partnerAddress?.administrative_area || partnerAddress?.postal_code;

      partnerContact = {
        type: 'contacts',
        bundle: 'student_record',
        field_attending_lessons: this.field_attending_lessons,
        field_home_phone: this.phoneFieldState.partner.home || this.filterPhoneNumber(this.partnerContact.value.field_home_phone, this.selectedCountryPartner as CountryCode),
        field_cell_phone: this.phoneFieldState.partner.cell || this.filterPhoneNumber(this.partnerContact.value.field_cell_phone, this.selectedCountryPartner as CountryCode),
        field_first_name: this.partnerContact.value.field_first_name,
        field_last_name: this.partnerContact.value.field_last_name,
        field_email: this.partnerContact.value.field_email,
        field_lead_follow: this.partnerContact.value.field_lead_follow_partner,
        field_communication_opt_in: this.field_communication_opt_in_partner ? "1" : "0",
        field_studio_reference: [this._authService.selectedStudio?.id],
        // field_notification_preference: this.partnerContact.value.field_notification_preference,
      };

      // Only include gender if it has a value (it's a boolean field)
      const genderPartner = this.partnerContact.value.field_gender_partner;
      if (genderPartner !== '' && genderPartner !== null && genderPartner !== undefined) {
        partnerContact.field_gender = genderPartner;
      }

      // Only include address if at least one field is filled
      if (hasPartnerAddress) {
        partnerContact.field_address = {
          address_line1: partnerAddress?.address_line1,
          locality: partnerAddress?.locality,
          administrative_area: partnerAddress?.administrative_area,
          postal_code: partnerAddress?.postal_code,
          country_code: partnerAddress?.country_code || this.selectedCountryPartner,
        };
      }
    }

    // Prep the data to be sent.
    const formattedInquired = moment(values.field_inquired).isValid()
      ? moment(values.field_inquired).format('YYYY-MM-DD')
      : moment().format('YYYY-MM-DD');

    let studentBody = {
      ...values,
      field_contacts: [mainContact, partnerContact].filter(contact => contact !== null),
      field_inquired: [{ value: formattedInquired }],
      field_executive: values?.schedule_first_lesson_field_executive,
      field_archive: "0",
      title: "Default Title",
      field_notes: values?.field_notes,
    };

    let schedule_first_lesson = this.scheduleFirstLessonEnabled;
    let create_de_enrollment = Boolean(schedule_first_lesson && studentBody?.['lesson_type'] == 560);
    let create_preoriginal_enrollment = Boolean(schedule_first_lesson && studentBody?.['lesson_type'] == 1393);
    let create_preoriginal_couple_enrollment = Boolean(schedule_first_lesson && studentBody?.['lesson_type'] == 1429);

    LoggerService.log('studentBody:', studentBody);
    LoggerService.log('schedule_first_lesson:', schedule_first_lesson);
    LoggerService.log('create_de_enrollment:', create_de_enrollment);
    LoggerService.log('create_preoriginal_enrollment:', create_preoriginal_enrollment);
    LoggerService.log('create_preoriginal_couple_enrollment:', create_preoriginal_couple_enrollment);

    await this.pullSystemPackages();

    try {
      const data = await this._entityRESTService.createEntity('student_accounts', 'student_account', studentBody, [{ 'parameter': '_format', 'value': 'json' }]).toPromise();
      this.studentAccountData = data;
      LoggerService.log('Student account created:', data);

      if (promptScheduleService) {
        LoggerService.log('Calling promptScheduleService');
        await this.promptScheduleService(data, requested_date_and_time, studentBody);
      }

      if (create_de_enrollment) {
        LoggerService.log('Creating DE enrollment');
        await new Promise<void>((resolve, reject) => {
          this.DEPackage$.subscribe(
            async (DE_package) => {
              LoggerService.log('DE package:', DE_package);
              if (DE_package) {
                try {
                  await this.createDEEnrollment(true, schedule_first_lesson, studentBody, requested_date_and_time, DE_package);
                  LoggerService.log('DE enrollment created');
                  // Close the dialog and refresh the calendar
                  this.displayProgressSpinner(false);
                  this.closeDialog();
                  await this.refreshCalendar2();

                  resolve();
                } catch (error) {
                  LoggerService.error('Error creating DE enrollment:', error);
                  reject(error);
                }
              } else {
                LoggerService.log('DE package not found');
                resolve();
              }
            },
            (error) => {
              LoggerService.error('Error retrieving DE package:', error);
              reject(error);
            }
          );
        });
      }

      if (create_preoriginal_enrollment) {
        LoggerService.log('Creating pre-original enrollment');
        await new Promise<void>((resolve, reject) => {
          this.PreOriginalPackage$.subscribe(
            async (PreOriginal_package) => {
              LoggerService.log('Pre-original package:', PreOriginal_package);
              if (PreOriginal_package) {
                try {
                  await this.createPreOriginalEnrollment(true, schedule_first_lesson, studentBody, requested_date_and_time, PreOriginal_package);
                  LoggerService.log('Pre-original enrollment created');
                  resolve();
                } catch (error) {
                  LoggerService.error('Error creating pre-original enrollment:', error);
                  reject(error);
                }
              } else {
                LoggerService.log('Pre-original package not found');
                resolve();
              }
            },
            (error) => {
              LoggerService.error('Error retrieving pre-original package:', error);
              reject(error);
            }
          );
        });
      }

      if (create_preoriginal_couple_enrollment) {
        LoggerService.log('Creating pre-original couple enrollment');
        await new Promise<void>((resolve, reject) => {
          this.PreOriginalCouplePackage$.subscribe(
            async (PreOriginalCouple_package) => {
              LoggerService.log('Pre-original couple package:', PreOriginalCouple_package);
              if (PreOriginalCouple_package) {
                try {
                  await this.createPreOriginalCoupleEnrollment(true, schedule_first_lesson, studentBody, requested_date_and_time, PreOriginalCouple_package);
                  LoggerService.log('Pre-original couple enrollment created');
                  resolve();
                } catch (error) {
                  LoggerService.error('Error creating pre-original couple enrollment:', error);
                  reject(error);
                }
              } else {
                LoggerService.log('Pre-original couple package not found');
                resolve();
              }
            },
            (error) => {
              LoggerService.error('Error retrieving pre-original couple package:', error);
              reject(error);
            }
          );
        });
      }

      LoggerService.log('Refreshing calendar');
      await this.refreshCalendar2();

      this.displayProgressSpinner(false);
      this.closeDialog();
      this.disableSubmitButton = false;
    } catch (error) {
      LoggerService.log('Error:', error);
      this.disableSubmitButton = false;
      this.handleError(error);
    }
  }

  studentUpdateLoad = (index: number): void => {
    this.editIndex = index;
    this.editMode = true;
    this.f.controls['__field_contact_inline_form'].patchValue(this.field_contacts[index])
  }

  studentRemove = (index: number): void => {
    // Reset the field contacts group control.
    this.f.form.controls['__field_contact_inline_form'].reset();
    this.editMode = false;
    this.field_contacts.splice(index, 1);
  }

  studentUpdateSubmit(number, contact: any) {
    this.field_contacts[this.editIndex] = contact;
    this.f.form.controls['__field_contact_inline_form'].reset();
    this.editMode = false;
  }

  studentIncrement(contact: any) {
    // Append type & bundle to model.
    contact = {
      ...contact,
      type: "contacts",
      bundle: "student_record",
      'field_studio_reference': [this._authService.selectedStudio?.id],
      field_communication_opt_in: contact.field_communication_opt_in ? "1" : "0"
    }

    // If our data binding fails us (API needs to give an array), set as an empty array.
    if (!Array.isArray(this.field_contacts)) { this.field_contacts = [] }

    this.field_contacts.push(contact);

    // Reset the field contact group control.
    this.f.form.controls['__field_contact_inline_form'].reset();
  }


  pullSystemPackages(): Promise<void> {
    // Pull the system packages from the /packages endpoint.
    let endpoint: string = "/api/v1/packages";
    let params = [
      { parameter: "field_retired_op", value: "=" },
      { parameter: "field_retired_value", value: "0" },
      { parameter: "field_is_system_package_op", value: "=" },
      { parameter: "field_is_system_package_value", value: "1" },
    ];

    return new Promise<void>((resolve, reject) => {
      this._drupalRESTService.httpGET(endpoint, params).subscribe(
        (data) => {
          let DE_package = null;
          let PreOriginal_package = null;
          let PreOriginalCouple_package = null;

          LoggerService.log('All packages from system packages API:', data?.['results']);

          // Several system packages can share an SPS code + lesson type (the
          // Global Guest package is PORI + Pre Original like Pre-Original), and
          // studios rename them freely, so the oldest match is the seeded one.
          const systemPackages = [...(data?.['results'] ?? [])]
            .sort((a, b) => Number(a.id) - Number(b.id));

          systemPackages.forEach(packageData => {
            LoggerService.log('Package SPS code:', packageData.field_sps_code, 'Package ID:', packageData.id, 'Package name:', packageData.field_package_name, 'Lesson type:', packageData.field_lesson_type);

            if (!DE_package && packageData.field_sps_code === 'DE') {
              DE_package = packageData;
            }
            if (!PreOriginal_package && packageData.field_sps_code === 'PORI' && packageData.field_lesson_type === 'Pre Original') {
              PreOriginal_package = packageData;
            }
            if (!PreOriginalCouple_package && packageData.field_sps_code === 'PORI' && packageData.field_lesson_type === 'Pre Original (Couple)') {
              PreOriginalCouple_package = packageData;
            }
          });

          LoggerService.log('DE package in pullSystemPackages:', DE_package);
          LoggerService.log('PreOriginal package in pullSystemPackages:', PreOriginal_package);
          LoggerService.log('PreOriginalCouple package in pullSystemPackages:', PreOriginalCouple_package);

          // Emit the packages through the subjects
          this.DEPackageSubject.next(DE_package);
          this.PreOriginalPackageSubject.next(PreOriginal_package);
          this.PreOriginalCouplePackageSubject.next(PreOriginalCouple_package);

          resolve();
        },
        (error) => {
          LoggerService.error('Error pulling system packages:', error);
          reject(error);
        }
      );
    });
  }


  scheduleFirstLesson(schedule_first_lesson: boolean, create_de_enrollment: boolean, create_preoriginal_enrollment: boolean, studentBody: any, requested_date_and_time: string) {
    // Convenience functionality to schedule first lesson.
    if (schedule_first_lesson && !create_de_enrollment && !create_preoriginal_enrollment) {
      let eventBody: any = {
        "field_student": [
          {
            "type": "attendees",
            "bundle": "attendance",
            "field_student_account": this.studentAccountData['id'][0].value,
            "field_students": this.studentAccountData?.['field_contacts']?.[0]?.target_id,
            "field_status": "64",
          }
        ],
        "field_type": studentBody['lesson_type'],
        "field_instructor": { target_id: studentBody['field_teacher'] },
        "field_date_and_time": requested_date_and_time,
      };

      // Get default duration times.
      let _duration = this.updateLessonDefaultTimes(studentBody['lesson_type']);
      eventBody = {
        ...eventBody,
        field_duration: _duration
      }

      // Create's the first event for the student.
      this._entityRESTService.createEntity('events', 'lesson', eventBody).subscribe(data => {
        this.refreshCalendar();
      }, error => {
        // TODO: Handle the error.
      });
    }
  }

  async createDEEnrollment(create_de_enrollment: boolean, schedule_first_lesson: boolean, studentBody: any, requested_date_and_time: string, DE_package) {
    LoggerService.log('createDEEnrollment called with:', {
      create_de_enrollment,
      schedule_first_lesson,
      studentBody,
      requested_date_and_time,
      DE_package
    });

    // Convenience functionality to create a DE enrollment.
    if (create_de_enrollment && schedule_first_lesson) {
      const dePackage = await this._entityRESTService.getEntity('packages', 'package', DE_package?.id).toPromise();

      // Read package properties properly
      const lessonPrice = parseFloat(dePackage?.['field_lesson_price']) || 0;
      const lessonCount = parseInt(dePackage?.['field_lesson_count']) || 1;
      const discountType = dePackage?.['field_discount_type'] || 'flat';
      const discountValue = parseFloat(dePackage?.['field_discount']) || 0;

      // Load the studio's tax rate from configuration
      let taxRate = 0;
      try {
        const taxConfig = await this._drupalRESTService.httpGET('/api_rest/v1/loadStudioConfig?config_field_name=field_tax_ref').toPromise();
        if (taxConfig && Array.isArray(taxConfig) && taxConfig.length > 0) {
          const currentDate = moment();
          // Find applicable taxes
          taxConfig.forEach(tax => {
            const startDate = tax.field_effective_tax_start_date?.[0]?.value
              ? moment(tax.field_effective_tax_start_date[0].value)
              : null;
            const endDate = tax.field_effective_tax_end_date?.[0]?.value
              ? moment(tax.field_effective_tax_end_date[0].value)
              : null;

            // Only include taxes that are currently effective
            if ((!startDate || currentDate.isSameOrAfter(startDate)) &&
                (!endDate || currentDate.isSameOrBefore(endDate))) {
              const calculationMethod = Array.isArray(tax.field_calculation_method)
                ? tax.field_calculation_method[0]?.value
                : tax.field_calculation_method;

              const taxValue = Array.isArray(tax.field_tax_value)
                ? parseFloat(tax.field_tax_value[0]?.value)
                : parseFloat(tax.field_tax_value);

              if (calculationMethod === 'PERCENTAGE' && !isNaN(taxValue)) {
                taxRate += taxValue;
              }
            }
          });
        }
      } catch (error) {
        LoggerService.error('Error loading tax rate:', error);
      }

      // Calculate total lessons price without discount
      const totalLessonsPrice = lessonPrice * lessonCount;

      // Calculate discount
      let discountAmount = 0;
      if (discountType === 'percentage') {
        discountAmount = totalLessonsPrice * (discountValue / 100);
      } else { // 'flat'
        discountAmount = discountValue;
      }

      // Calculate price after discount
      const priceAfterDiscount = totalLessonsPrice - discountAmount;

      // Calculate tax amount
      const taxAmount = priceAfterDiscount * (taxRate / 100);

      // Total with tax
      const totalWithTax = priceAfterDiscount + taxAmount;

      LoggerService.log('DE Package:', dePackage);
      LoggerService.log('DE Package Price:', lessonPrice);
      LoggerService.log('Discount:', discountAmount);
      LoggerService.log('Tax:', taxAmount);

      let enrollmentBody = {
        "field_draft": false,
        "field_student": this.studentAccountData['id'][0].value,
        "field_sale_date": moment(this._field_sale_date).format('YYYY-MM-DDTHH:mm:ss'),
        "field_expiration_date": moment(this._field_expiration_date).add(4, 'weeks').format('YYYY-MM-DD'),
        "field_category": "1395",
        "field_enrollment_package_name": dePackage?.['id'],
        "field_enrollment_lesson_price": lessonPrice,
        "field_enrollment_lesson_count": lessonCount,
        "field_total_lessons_price": totalLessonsPrice,
        "field_enrollment_total_price": priceAfterDiscount,
        "field_total_price_with_est_tax": totalWithTax,
        "field_taxes": taxAmount,
        "field_tax_percentage": taxRate,
        "field_total_paid": null,
        "field_lesson_used": null,
        "field_lessons_paid": null,
        "field_lesson_available": null,
        "field_payments_structure": "paid_in_full",
        "field_executive": studentBody?.schedule_first_lesson_field_executive,
        "field_junior_executive": null,
        "field_scheduled_payments": [
          {
            "field_amount_paid": null,
            "field_enrollment": null,
            "field_paid_in_full": true,
            "field_down_payment": true,
            "field_payment_amount": priceAfterDiscount,
            "field_tax_amount": taxAmount,
            "field_payment_date": moment(this._field_sale_date).format("YYYY-MM-DD"),
            "field_studio_reference": this._authService.selectedStudio?.id,
            "bundle": "scheduled_payments",
            "type": "scheduled_payments"
          }
        ],
        "field_instructor_percentages": [
          {
            "field_amount": priceAfterDiscount,
            "field_instructor": studentBody['field_teacher'],
            "field_percentage": 100,
            "field_studio_reference": this._authService.selectedStudio?.id,
            "bundle": "instructor_percentages",
            "type": "instructor_percentages"
          }
        ],
        "field_interval_unit": "",
        "field_discount": discountValue,
        "field_discount_type": discountType,
        "field_enrollment_status": "Open",
        "field_next_scheduled_payment": "",
        "field_visibility": false,
        "field_notes": ""
      };

      LoggerService.log('Enrollment Body:', enrollmentBody);

      // Create the enrollment for the student.
      const enrollmentData = await this._entityRESTService.createEntity('packages', 'enrollment', enrollmentBody).toPromise();
      LoggerService.log('Enrollment Data:', enrollmentData);

      // After the enrollment is created, create the first lesson.
      //  - Convenience functionality to schedule first lesson.
      if (!schedule_first_lesson) return;

      let eventBody = {
        "field_student": [
          {
            "type": "attendees",
            "bundle": "attendance",
            "field_status": "64",
            "field_student_account": this.studentAccountData['id'][0].value,
            "field_students": (this.studentAccountData?.['field_contacts']?.[1]?.target_id) ? [this.studentAccountData['field_contacts'][0].target_id, this.studentAccountData['field_contacts'][1].target_id] : this.studentAccountData['field_contacts'][0].target_id, // field_contacts, default to the couple value.
            "field_enrollment": { target_id: enrollmentData['id'][0]['value'] } // Add enrollment here.
          }
        ],
        "field_type": studentBody['lesson_type'],
        "field_instructor": { target_id: studentBody['field_teacher'] }, // data['field_teacher'][0].target_id
        "field_duration": "00:45",
        "field_date_and_time": requested_date_and_time,
      };

      // Get default duration times.
      let _duration = this.updateLessonDefaultTimes(studentBody['lesson_type']);
      eventBody = {
        ...eventBody,
        field_duration: _duration
      }

      LoggerService.log('Event Body:', eventBody);

      // Create's the first event for the student.
      const eventData = await this._entityRESTService.createEntity('events', 'lesson', eventBody).toPromise();
      LoggerService.log('Event Data:', eventData);

      // Create the DE Pre Chat for the student.
      let DEServiceBody = {
        'field_type': 1404,
        'field_status': 106,
        // 'field_executive': studentBody?.field_teacher,
        'field_executive': studentBody?.schedule_first_lesson_field_executive,
        'field_duration': '00:15',
        'field_date_and_time': moment(requested_date_and_time).subtract(15, 'minutes').format('YYYY-MM-DDTHH:mm:ss'),
        'field_student': [
          {
            type: 'attendees',
            bundle: 'attendance',
            field_student_account: this.studentAccountData['id'][0].value,
            field_students: (this.studentAccountData?.['field_contacts']?.[1]?.target_id) ? [this.studentAccountData['field_contacts'][0].target_id, this.studentAccountData['field_contacts'][1].target_id] : this.studentAccountData['field_contacts'][0].target_id, // field_contacts, default to the couple value.
          },
        ],
      }

      LoggerService.log('DE Service Body:', DEServiceBody);

      const DEServiceData = await this._entityRESTService.createEntity('events', 'services', DEServiceBody).toPromise();
      LoggerService.log('DE Service Data:', DEServiceData);

      // Create the DE Pre Chat for the student.
      let OriginalChatServiceBody = {
        'field_type': 567,
        'field_status': 113,
        // 'field_executive': studentBody?.field_teacher,
        'field_executive': studentBody?.schedule_first_lesson_field_executive,
        'field_duration': '00:15',
        'field_date_and_time': moment(requested_date_and_time).add(30, 'minutes').format('YYYY-MM-DDTHH:mm:ss'),
        'field_student': [
          {
            type: 'attendees',
            bundle: 'attendance',
            field_student_account: this.studentAccountData['id'][0].value,
            field_students: (this.studentAccountData?.['field_contacts']?.[1]?.target_id) ? [this.studentAccountData['field_contacts'][0].target_id, this.studentAccountData['field_contacts'][1].target_id] : this.studentAccountData['field_contacts'][0].target_id, // field_contacts, default to the couple value.
          },
        ],
      }

      LoggerService.log('Original Chat Service Body:', OriginalChatServiceBody);

      const OriginalChatServiceData = await this._entityRESTService.createEntity('events', 'services', OriginalChatServiceBody).toPromise();
      LoggerService.log('Original Chat Service Data:', OriginalChatServiceData);

      await this.refreshCalendar2();
    }

    return Promise.resolve();
  }

  async createPreOriginalEnrollment(create_preoriginal_enrollment: boolean, schedule_first_lesson: boolean, studentBody: any, requested_date_and_time: string, PreOriginal_package) {
    if (create_preoriginal_enrollment && schedule_first_lesson) {
      // First, load the pre original package because we need price data.
      const preOriginalPackage = await this._entityRESTService.getEntity('packages', 'package', PreOriginal_package?.id).toPromise();

      // Read package properties properly
      const lessonPrice = parseFloat(preOriginalPackage?.['field_lesson_price']) || 25;
      const lessonCount = parseInt(preOriginalPackage?.['field_lesson_count']) || 1;
      const discountType = preOriginalPackage?.['field_discount_type'] || 'flat';
      const discountValue = parseFloat(preOriginalPackage?.['field_discount']) || 0;

      // Load the studio's tax rate from configuration
      let taxRate = 0;
      try {
        const taxConfig = await this._drupalRESTService.httpGET('/api_rest/v1/loadStudioConfig?config_field_name=field_tax_ref').toPromise();
        if (taxConfig && Array.isArray(taxConfig) && taxConfig.length > 0) {
          const currentDate = moment();
          // Find applicable taxes
          taxConfig.forEach(tax => {
            const startDate = tax.field_effective_tax_start_date?.[0]?.value
              ? moment(tax.field_effective_tax_start_date[0].value)
              : null;
            const endDate = tax.field_effective_tax_end_date?.[0]?.value
              ? moment(tax.field_effective_tax_end_date[0].value)
              : null;

            // Only include taxes that are currently effective
            if ((!startDate || currentDate.isSameOrAfter(startDate)) &&
                (!endDate || currentDate.isSameOrBefore(endDate))) {
              const calculationMethod = Array.isArray(tax.field_calculation_method)
                ? tax.field_calculation_method[0]?.value
                : tax.field_calculation_method;

              const taxValue = Array.isArray(tax.field_tax_value)
                ? parseFloat(tax.field_tax_value[0]?.value)
                : parseFloat(tax.field_tax_value);

              if (calculationMethod === 'PERCENTAGE' && !isNaN(taxValue)) {
                taxRate += taxValue;
              }
            }
          });
        }
      } catch (error) {
        LoggerService.error('Error loading tax rate:', error);
      }

      // Calculate total lessons price without discount
      const totalLessonsPrice = lessonPrice * lessonCount;

      // Calculate discount
      let discountAmount = 0;
      if (discountType === 'percentage') {
        discountAmount = totalLessonsPrice * (discountValue / 100);
      } else { // 'flat'
        discountAmount = discountValue;
      }

      // Calculate price after discount
      const priceAfterDiscount = totalLessonsPrice - discountAmount;

      // Calculate tax amount
      const taxAmount = priceAfterDiscount * (taxRate / 100);

      // Total with tax
      const totalWithTax = priceAfterDiscount + taxAmount;

      let enrollmentBody =
      {
        "field_draft": false,
        "field_student": this.studentAccountData['id'][0].value,
        "field_sale_date": moment(this._field_sale_date).format("YYYY-MM-DDTHH:mm:ss"),
        "field_expiration_date": moment(this._field_expiration_date).add(4, 'weeks').format('YYYY-MM-DD'),
        "field_category": "49",
        "field_enrollment_package_name": preOriginalPackage?.['id'],
        "field_enrollment_lesson_price": lessonPrice,
        "field_enrollment_lesson_count": lessonCount,
        "field_total_lessons_price": totalLessonsPrice,
        "field_enrollment_total_price": priceAfterDiscount,
        "field_total_price_with_est_tax": totalWithTax,
        "field_taxes": taxAmount,
        "field_tax_percentage": taxRate,
        "field_total_paid": null,
        "field_lesson_used": null,
        "field_lessons_paid": null,
        "field_lesson_available": null,
        "field_payments_structure": "paid_in_full",
        "field_executive": studentBody?.schedule_first_lesson_field_executive,
        "field_junior_executive": null,
        "field_scheduled_payments": [
          {
            "field_amount_paid": null,
            "field_enrollment": null,
            "field_paid_in_full": true,
            "field_down_payment": true,
            "field_payment_amount": priceAfterDiscount,
            "field_tax_amount": taxAmount,
            "field_payment_date": moment(this._field_sale_date).format("YYYY-MM-DD"),
            "field_studio_reference": this._authService.selectedStudio?.id,
            "bundle": "scheduled_payments",
            "type": "scheduled_payments"
          }
        ],
        "field_instructor_percentages": [
          {
            "field_amount": priceAfterDiscount,
            "field_instructor": studentBody['field_teacher'],
            "field_percentage": 100,
            "field_studio_reference": this._authService.selectedStudio?.id,
            "bundle": "instructor_percentages",
            "type": "instructor_percentages"
          }
        ],
        "field_interval_unit": "",
        "field_discount": discountValue,
        "field_discount_type": discountType,
        "field_enrollment_status": "Open",
        "field_next_scheduled_payment": "",
        "field_visibility": false,
        "field_notes": ""
      };

      // Create the enrollment for the student.
      const enrollmentData = await this._entityRESTService.createEntity('packages', 'enrollment', enrollmentBody).toPromise();

      // After the enrollment is created, create the first lesson.
      //  - Convenience functionality to schedule first lesson.
      if (!schedule_first_lesson) return;
      let eventBody = {
        "field_student": [
          {
            "type": "attendees",
            "bundle": "attendance",
            "field_student_account": this.studentAccountData['id'][0].value,
            "field_students": (this.studentAccountData?.['field_contacts']?.[1]?.target_id) ? [this.studentAccountData['field_contacts'][0].target_id, this.studentAccountData['field_contacts'][1].target_id] : this.studentAccountData['field_contacts'][0].target_id, // field_contacts, default to the couple value.
            "field_status": "64",
            "field_enrollment": { target_id: enrollmentData['id'][0]['value'] } // Add enrollment here.
          }
        ],
        "field_type": studentBody['lesson_type'],
        "field_instructor": { target_id: studentBody['field_teacher'] }, // data['field_teacher'][0].target_id
        "field_duration": "00:45",
        "field_date_and_time": requested_date_and_time,
      };

      // Get default duration times.
      let _duration = this.updateLessonDefaultTimes(studentBody['lesson_type']);
      eventBody = {
        ...eventBody,
        field_duration: _duration
      }

      // Create's the first event for the student.
      await this._entityRESTService.createEntity('events', 'lesson', eventBody).toPromise();

      // Create the Pre Chat for the student.
      let PREServiceBody = {
        'field_type': 656,
        'field_status': 106,
        // 'field_executive': studentBody?.field_teacher,
        'field_executive': studentBody?.schedule_first_lesson_field_executive,
        'field_duration': '00:15',
        'field_date_and_time': moment(requested_date_and_time).subtract(15, 'minutes').format('YYYY-MM-DDTHH:mm:ss'),
        'field_student': [
          {
            type: 'attendees',
            bundle: 'attendance',
            field_student_account: this.studentAccountData['id'][0].value,
            field_students: (this.studentAccountData?.['field_contacts']?.[1]?.target_id) ? [this.studentAccountData['field_contacts'][0].target_id, this.studentAccountData['field_contacts'][1].target_id] : this.studentAccountData['field_contacts'][0].target_id, // field_contacts, default to the couple value.
          },
        ],
      }

      await this._entityRESTService.createEntity('events', 'services', PREServiceBody).toPromise();
      // Create the DE Pre Chat for the student.
      let OriginalChatServiceBody = {
        'field_type': 567,
        'field_status': 113,
        // 'field_executive': studentBody?.field_teacher,
        'field_executive': studentBody?.schedule_first_lesson_field_executive,
        'field_duration': '00:15',
        'field_date_and_time': moment(requested_date_and_time).add(30, 'minutes').format('YYYY-MM-DDTHH:mm:ss'),
        'field_student': [
          {
            type: 'attendees',
            bundle: 'attendance',
            field_student_account: this.studentAccountData['id'][0].value,
            field_students: (this.studentAccountData?.['field_contacts']?.[1]?.target_id) ? [this.studentAccountData['field_contacts'][0].target_id, this.studentAccountData['field_contacts'][1].target_id] : this.studentAccountData['field_contacts'][0].target_id, // field_contacts, default to the couple value.
          },
        ],
      }

      await this._entityRESTService.createEntity('events', 'services', OriginalChatServiceBody).toPromise();
    }

    return Promise.resolve();
  }

  async createPreOriginalCoupleEnrollment(create_preoriginal_couple_enrollment: boolean, schedule_first_lesson: boolean, studentBody: any, requested_date_and_time: string, PreOriginalCouple_package) {
    if (create_preoriginal_couple_enrollment && schedule_first_lesson) {
      // First, load the pre original couple package because we need price data.
      const preOriginalCouplePackage = await this._entityRESTService.getEntity('packages', 'package', PreOriginalCouple_package?.id).toPromise();

      // Read package properties properly
      const lessonPrice = parseFloat(preOriginalCouplePackage?.['field_lesson_price']) || 25;
      const lessonCount = parseInt(preOriginalCouplePackage?.['field_lesson_count']) || 1;
      const discountType = preOriginalCouplePackage?.['field_discount_type'] || 'flat';
      const discountValue = parseFloat(preOriginalCouplePackage?.['field_discount']) || 0;

      // Load the studio's tax rate from configuration
      let taxRate = 0;
      try {
        const taxConfig = await this._drupalRESTService.httpGET('/api_rest/v1/loadStudioConfig?config_field_name=field_tax_ref').toPromise();
        if (taxConfig && Array.isArray(taxConfig) && taxConfig.length > 0) {
          const currentDate = moment();
          // Find applicable taxes
          taxConfig.forEach(tax => {
            const startDate = tax.field_effective_tax_start_date?.[0]?.value
              ? moment(tax.field_effective_tax_start_date[0].value)
              : null;
            const endDate = tax.field_effective_tax_end_date?.[0]?.value
              ? moment(tax.field_effective_tax_end_date[0].value)
              : null;

            // Only include taxes that are currently effective
            if ((!startDate || currentDate.isSameOrAfter(startDate)) &&
                (!endDate || currentDate.isSameOrBefore(endDate))) {
              const calculationMethod = Array.isArray(tax.field_calculation_method)
                ? tax.field_calculation_method[0]?.value
                : tax.field_calculation_method;

              const taxValue = Array.isArray(tax.field_tax_value)
                ? parseFloat(tax.field_tax_value[0]?.value)
                : parseFloat(tax.field_tax_value);

              if (calculationMethod === 'PERCENTAGE' && !isNaN(taxValue)) {
                taxRate += taxValue;
              }
            }
          });
        }
      } catch (error) {
        LoggerService.error('Error loading tax rate:', error);
      }

      // Calculate total lessons price without discount
      const totalLessonsPrice = lessonPrice * lessonCount;

      // Calculate discount
      let discountAmount = 0;
      if (discountType === 'percentage') {
        discountAmount = totalLessonsPrice * (discountValue / 100);
      } else { // 'flat'
        discountAmount = discountValue;
      }

      // Calculate price after discount
      const priceAfterDiscount = totalLessonsPrice - discountAmount;

      // Calculate tax amount
      const taxAmount = priceAfterDiscount * (taxRate / 100);

      // Total with tax
      const totalWithTax = priceAfterDiscount + taxAmount;

      let enrollmentBody =
      {
        "field_draft": false,
        "field_student": this.studentAccountData['id'][0].value,
        "field_sale_date": moment(this._field_sale_date).format("YYYY-MM-DDTHH:mm:ss"),
        "field_expiration_date": moment(this._field_expiration_date).add(4, 'weeks').format('YYYY-MM-DD'),
        "field_category": "49", // Pre-Original category (same as regular Pre-Original)
        "field_enrollment_package_name": preOriginalCouplePackage?.['id'],
        "field_enrollment_lesson_price": lessonPrice,
        "field_enrollment_lesson_count": lessonCount,
        "field_total_lessons_price": totalLessonsPrice,
        "field_enrollment_total_price": priceAfterDiscount,
        "field_total_price_with_est_tax": totalWithTax,
        "field_taxes": taxAmount,
        "field_tax_percentage": taxRate,
        "field_total_paid": null,
        "field_lesson_used": null,
        "field_lessons_paid": null,
        "field_lesson_available": null,
        "field_payments_structure": "paid_in_full",
        "field_executive": studentBody?.schedule_first_lesson_field_executive,
        "field_junior_executive": null,
        "field_scheduled_payments": [
          {
            "field_amount_paid": null,
            "field_enrollment": null,
            "field_paid_in_full": true,
            "field_down_payment": true,
            "field_payment_amount": priceAfterDiscount,
            "field_tax_amount": taxAmount,
            "field_payment_date": moment(this._field_sale_date).format("YYYY-MM-DD"),
            "field_studio_reference": this._authService.selectedStudio?.id,
            "bundle": "scheduled_payments",
            "type": "scheduled_payments"
          }
        ],
        "field_instructor_percentages": [
          {
            "field_amount": priceAfterDiscount,
            "field_instructor": studentBody['field_teacher'],
            "field_percentage": 100,
            "field_studio_reference": this._authService.selectedStudio?.id,
            "bundle": "instructor_percentages",
            "type": "instructor_percentages"
          }
        ],
        "field_interval_unit": "",
        "field_discount": discountValue,
        "field_discount_type": discountType,
        "field_enrollment_status": "Open",
        "field_next_scheduled_payment": "",
        "field_visibility": false,
        "field_notes": ""
      };

      // Create the enrollment for the student.
      const enrollmentData = await this._entityRESTService.createEntity('packages', 'enrollment', enrollmentBody).toPromise();

      // After the enrollment is created, create the first lesson.
      //  - Convenience functionality to schedule first lesson.
      if (!schedule_first_lesson) return;
      let eventBody = {
        "field_student": [
          {
            "type": "attendees",
            "bundle": "attendance",
            "field_student_account": this.studentAccountData['id'][0].value,
            "field_students": (this.studentAccountData?.['field_contacts']?.[1]?.target_id) ? [this.studentAccountData['field_contacts'][0].target_id, this.studentAccountData['field_contacts'][1].target_id] : this.studentAccountData['field_contacts'][0].target_id, // field_contacts, default to the couple value.
            "field_status": "64",
            "field_enrollment": { target_id: enrollmentData['id'][0]['value'] } // Add enrollment here.
          }
        ],
        "field_type": "1393", // Use the actual Pre Original lesson type ID for the event
        "field_instructor": { target_id: studentBody['field_teacher'] }, // data['field_teacher'][0].target_id
        "field_duration": "00:45",
        "field_date_and_time": requested_date_and_time,
      };

      // Get default duration times.
      let _duration = this.updateLessonDefaultTimes("1393"); // Use actual lesson type for duration
      eventBody = {
        ...eventBody,
        field_duration: _duration
      }

      // Create's the first event for the student.
      await this._entityRESTService.createEntity('events', 'lesson', eventBody).toPromise();

      // Create the Pre Chat for the student.
      let PREServiceBody = {
        'field_type': 656,
        'field_status': 106,
        'field_executive': studentBody?.schedule_first_lesson_field_executive,
        'field_duration': '00:15',
        'field_date_and_time': moment(requested_date_and_time).subtract(15, 'minutes').format('YYYY-MM-DDTHH:mm:ss'),
        'field_student': [
          {
            type: 'attendees',
            bundle: 'attendance',
            field_student_account: this.studentAccountData['id'][0].value,
            field_students: (this.studentAccountData?.['field_contacts']?.[1]?.target_id) ? [this.studentAccountData['field_contacts'][0].target_id, this.studentAccountData['field_contacts'][1].target_id] : this.studentAccountData['field_contacts'][0].target_id, // field_contacts, default to the couple value.
          },
        ],
      }

      await this._entityRESTService.createEntity('events', 'services', PREServiceBody).toPromise();

      // Create the Original Chat service
      let OriginalChatServiceBody = {
        'field_type': 567,
        'field_status': 113,
        'field_executive': studentBody?.schedule_first_lesson_field_executive,
        'field_duration': '00:15',
        'field_date_and_time': moment(requested_date_and_time).add(30, 'minutes').format('YYYY-MM-DDTHH:mm:ss'),
        'field_student': [
          {
            type: 'attendees',
            bundle: 'attendance',
            field_student_account: this.studentAccountData['id'][0].value,
            field_students: (this.studentAccountData?.['field_contacts']?.[1]?.target_id) ? [this.studentAccountData['field_contacts'][0].target_id, this.studentAccountData['field_contacts'][1].target_id] : this.studentAccountData['field_contacts'][0].target_id, // field_contacts, default to the couple value.
          },
        ],
      }

      await this._entityRESTService.createEntity('events', 'services', OriginalChatServiceBody).toPromise();
    }

    return Promise.resolve();
  }

  promptScheduleService(data, requested_date_and_time, studentBody) {
    // Potentially have more than one student in the service.
    let field_students = [];
    data['field_contacts'].forEach(contact => {
      field_students.push(contact.target_id.toString())
    });

    // Date and time should be 15 minutes before the scheduled lesson.
    let field_date_and_time = moment(requested_date_and_time).subtract(15, 'minutes');;

    let fieldsData = {
      'field_inquiry_taker': studentBody?.field_inquiry_taker,
      'field_duration': '00:15',
      'field_date_and_time': field_date_and_time,
      '___field_student': {
        type: 'attendees',
        bundle: 'attendance',
        field_student_account: data['title'][0].value.toString() + ' (' + data['id'][0].value.toString() + ') ',
        field_students: field_students.toString(),
      },
    }

    this._dialogService.openDialog(EventServicesEntityComponent, "defaultWithData", {
      data: {
        EntityID: null,
        eckType: 'events',
        bundle: 'services',
        action: 'create',
        fieldsData: fieldsData ?? '',
      },
    }).afterClosed().subscribe(data => {
    });

    return Promise.resolve();
  }

  getFilteredLessonTypes() {
    // Return cached result if available
    if (this._filteredLessonTypes) {
      return this._filteredLessonTypes;
    }

    // Only allow Pre Original and Dance Evaluation lesson types (consistent with booking component)
    const allowedLessonTypeNames = ['Pre Original', 'Dance Evaluation (DE)', 'Pre Original (Couple)'];
    const filteredTypes = this._taxonomyService.lesson_type.filter(lt => allowedLessonTypeNames.includes(lt.name));

    // Cache the result
    this._filteredLessonTypes = filteredTypes;
    return this._filteredLessonTypes;
  }

  toggleInquiry() {
    this.f.form.patchValue({ 'lesson_type': null });
  }

  async checkScheduleConflicts() {
    if (this.scheduleConflicts) {
      await this.scheduleConflicts.checkScheduleConflicts();
    }
  }

  scrollToConflicts() {
    setTimeout(() => {
      this.scheduleConflicts.conflictsContainer.nativeElement.scrollIntoView({ behavior: 'smooth' });
    }, 0);
  }

  isFormValid(): boolean {
    if (!this.formElement) return false;

    // Basic validation for main contact
    if (!this.firstContact?.valid) {
      return false;
    }

    // Partner validation if attending with partner
    if (this.field_attending_lessons === 'partner' && !this.partnerContact?.valid) {
      return false;
    }

    // Check field_inquiry_taker
    const inquiryTakerControl = this.formElement.form.get('field_inquiry_taker');
    if (!inquiryTakerControl?.value) {
      return false;
    }

    // If scheduling first lesson is enabled, validate required fields
    if (this.scheduleFirstLessonEnabled) {
      // Check if date and time is selected
      const dateTimeControl = this.formElement.form.get('requested_date_time');
      if (!dateTimeControl?.valid) {
        return false;
      }

      // Check required fields
      const executiveControl = this.formElement.form.get('schedule_first_lesson_field_executive');
      const teacherControl = this.formElement.form.get('field_teacher');
      const lessonTypeControl = this.formElement.form.get('lesson_type');

      if (!executiveControl?.valid || !teacherControl?.valid || !lessonTypeControl?.valid) {
        return false;
      }
    }

    return true;
  }

  onAttendingLessonsChange(event: any) {
    this.field_attending_lessons = event.value;
  }

  copyAddress() {
    if (this.firstContact && this.partnerContact) {
      const firstContactAddress = this.firstContact.control.get('field_address').value;
      this.partnerContact.control.get('field_address').setValue({
        multiple_properties: 'true',
        address_line1: firstContactAddress?.address_line1,
        locality: firstContactAddress?.locality,
        administrative_area: firstContactAddress?.administrative_area,
        postal_code: firstContactAddress?.postal_code,
        country_code: firstContactAddress?.country_code || 'US',
      });
    }
  }

  onPhoneFormatted(contact: 'main' | 'partner', field: 'home' | 'cell', value: E164Number | null) {
    this.phoneFieldState[contact][field] = value;
  }

  checkDuplicateStudent(contactType: 'main' | 'partner') {
    const formGroup = contactType === 'main' ? this.firstContact : this.partnerContact;
    const firstName = formGroup.control.get('field_first_name')?.value;
    const lastName = formGroup.control.get('field_last_name')?.value;

    if (!firstName || !lastName) {
      return;
    }

    const params = [
        { parameter: 'field_first_name', value: firstName },
        { parameter: 'field_last_name', value: lastName }
    ];

    this._drupalRESTService.httpGET('/api_rest/v1/checkDuplicateStudent', params).subscribe(
      (response: any) => {
        if (response.warning) {
          if (contactType === 'main') {
            this.duplicateWarning = response.warning;
          } else {
            this.duplicateWarningPartner = response.warning;
          }
        } else {
          if (contactType === 'main') {
            this.duplicateWarning = '';
          } else {
            this.duplicateWarningPartner = '';
          }
        }
      },
      error => {
        LoggerService.error('Error checking for duplicate student:', error);
      }
    );
  }

  override onDateTimeInputClick() {
    // If field is empty, set default to today at 12:00 PM
    if (!this.field_date_and_time) {
      const today = moment().hour(12).minute(0).second(0);
      this.field_date_and_time = today.format('YYYY-MM-DD[T]HH:mm:ss');
      this.pickerFieldDateTime._selected = today;
      this.f.form.controls['requested_date_time'].patchValue(this.field_date_and_time);
    } else if (moment(this.field_date_and_time, moment.ISO_8601, true).isValid()) {
      const dateTime = moment(this.field_date_and_time);
      this.pickerFieldDateTime._selected = dateTime;
    }
    this.pickerFieldDateTime.open();
  }

  /**
   * A calendar slot passes its column's user as field_teacher. Keep it only
   * when that user is an active instructor in the Teacher list; otherwise
   * (e.g. the Website Booking column) the select would show blank but still
   * submit the hidden value as the first lesson's instructor.
   */
  override _loadFieldsData(fieldsData, formRef = null) {
    const data = this.injectedDialogData?.['data']?.['fieldsData'];
    if (!data) {
      return;
    }
    const { field_teacher, ...rest } = data;
    const teacher = field_teacher
      ? (this._fieldsService.instructorData() || []).find(element => String(element.uid) === String(field_teacher) && element.status !== '0')
      : null;
    setTimeout(() => {
      this?.f?.form?.patchValue(teacher ? { ...rest, field_teacher: teacher.uid } : rest);
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();

    // Manually inject the service - try multiple injection points
    if (!this.internationalAddressService) {
      const injector = (this as any)._injector ||
                       (this as any)._changeDetectorRef?.injector ||
                       (this as any)._el?.nativeElement?.injector;

      if (injector) {
        try {
          this.internationalAddressService = injector.get(InternationalAddressService);
        } catch (e) {
          LoggerService.error('Could not inject InternationalAddressService:', e);
          // Fallback: create instance directly
          this.internationalAddressService = new InternationalAddressService();
        }
      } else {
        // Fallback: create instance directly
        this.internationalAddressService = new InternationalAddressService();
      }
    }

    // Load studio country to default the address country field
    this.loadStudioCountry();
  }

  /**
   * Load the studio's country setting to use as default for student addresses
   */
  loadStudioCountry(): void {
    const configFieldName = 'field_studio_information_ref';
    this._drupalRESTService
      .httpGET(`/api_rest/v1/loadStudioConfig?config_field_name=${configFieldName}`)
      .subscribe(
        (response) => {
          const studioCountry = response?.['field_studio_address']?.[0]?.country_code;
          if (studioCountry) {
            this.selectedCountry = studioCountry;
            this.selectedCountryPartner = studioCountry;
            this.onCountryChange(studioCountry);
            this.onCountryChangePartner(studioCountry);
          }
        },
        (error) => {
          LoggerService.error('Error loading studio country:', error);
          // Keep default US if loading fails
        }
      );
  }

}
